import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'
import type { SearchType } from './scopeTokens'

/**
 * usePlatformSearch — thin async client for the universal graph `search` query.
 *
 * I unapolegtically stole the best bits from the suggestions implementation while doing this. We'll be able to replace
 * a lot of these types once adc/sdk naturally bumps. I did the graph changes at the same time so they weren't quite
 * ready to use since they both landed together.
 */

export interface PlatformSearchResult {
  readonly type: string
  readonly label: string
  readonly sublabel?: string
  readonly relates_to_urn: string
  readonly score: number
}

export interface PlatformSearchGroup {
  readonly type: string
  readonly label: string
  readonly total: number
  readonly results: ReadonlyArray<PlatformSearchResult>
}

export interface PlatformSearchClassification {
  readonly detected: string | null
  readonly confidence: number
  readonly auto_target: boolean
  readonly scoped: boolean
}

interface UsePlatformSearchOptions {
  debounceMs?: number
  minSearchLength?: number
}

interface SearchApiResponse {
  classification: PlatformSearchClassification | null
  groups: PlatformSearchGroup[]
}

const SEARCH_QUERY = gql`
  query morpheus_command_search($text: String, $searchTypes: [SearchType!]) {
    search(text: $text, searchTypes: $searchTypes) {
      classification {
        detected
        confidence
        auto_target
        scoped
      }

      groups {
        type
        label
        total
        results {
          __typename
          type
          label
          sublabel
          relates_to_urn
          score
        }
      }
    }
  }
`

export function usePlatformSearch({
  debounceMs = 200,
  minSearchLength = 2,
}: UsePlatformSearchOptions = {}): {
  groups: ReadonlyArray<PlatformSearchGroup>
  classification: PlatformSearchClassification | null
  isSearching: boolean
  search: (text: string, searchTypes?: SearchType[]) => void
  clear: () => void
} {
  const [groups, setGroups] = useState<ReadonlyArray<PlatformSearchGroup>>([])
  const [classification, setClassification] =
    useState<PlatformSearchClassification | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  // Discard out-of-order responses: each request closes over an incrementing
  // id and bails if a newer request has started since. Mirrors the
  // `fetchIdRef` pattern in `@adc/blueprint`'s intake hooks (there's no shared
  // hook for it — it's an inline convention).
  const fetchIdRef = useRef(0)

  const clearState = useCallback((): void => {
    setGroups([])
    setClassification(null)
    setIsSearching(false)
  }, [])

  // Debounced network call, kept behind `useMemo` so the timer is stable
  // across renders — same shape as blueprint's `debouncedGetSuggestions`
  // (`_.debounce(fn, ms)`), rather than hand-rolled setTimeout bookkeeping.
  const runQuery = useMemo(
    () =>
      debounce((trimmed: string, searchTypes?: SearchType[]): void => {
        const thisFetchId = ++fetchIdRef.current
        setIsSearching(true)
        fetch
          .forAuctionGraph<'search', SearchApiResponse>({
            query: SEARCH_QUERY,
            variables: { text: trimmed, searchTypes },
          })
          .then(res => {
            if (thisFetchId !== fetchIdRef.current) return
            const data = res.body.data.search
            setGroups(data?.groups ?? [])
            setClassification(data?.classification ?? null)
          })
          .catch(() => {
            if (thisFetchId !== fetchIdRef.current) return
            clearState()
          })
          .finally(() => {
            if (thisFetchId === fetchIdRef.current) setIsSearching(false)
          })
      }, debounceMs),
    [debounceMs, clearState]
  )

  // Cancel a pending debounced call on unmount.
  useEffect(() => () => runQuery.cancel(), [runQuery])

  const clear = useCallback((): void => {
    runQuery.cancel()
    // Bump the id so any in-flight response is ignored once it lands.
    fetchIdRef.current++
    clearState()
  }, [runQuery, clearState])

  const search = useCallback(
    (text: string, searchTypes?: SearchType[]): void => {
      const trimmed = (text || '').trim()
      // A scope with no text still shouldn't fire until there's a query.
      if (trimmed.length < minSearchLength) {
        clear()
        return
      }
      runQuery(trimmed, searchTypes)
    },
    [runQuery, minSearchLength, clear]
  )

  return { groups, classification, isSearching, search, clear }
}
